// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Framework product pages (store front end). The framework itself is
// licensed separately under the Squarebit Framework End User License
// Agreement: https://store.squarebitstudios.com/sbframework/eula
// ==========================================================================

import React from 'react';
import { toast } from 'react-hot-toast';
import { FaWindows, FaApple } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { managementService } from '../management/service/management.service';
import { ProductChangelog } from '../common/components/product-changelog.component';
import { NotFound } from '../common/components/404.component';
import { useHasFrameworkAccess } from './feature/use-sbframework-access';
import pkg from '../../../package.json';
import { useDownloadTracker } from '../common/hooks/use-download-tracker';

/**
 * The framework's changelog, and the target of the "Update Available" entry in
 * the Maya menu.
 *
 * Grant-gated like the rest of the product: an account without `sbframework`
 * gets the ordinary not-found page, not a 403, so the route never confirms
 * there is something behind it. The changelog markdown is served from /public
 * and so is technically fetchable by anyone who guesses the filename - which is
 * why the gate here is about not ADVERTISING the product rather than about
 * keeping release notes secret. The download behind it is the thing that is
 * actually protected, and that check is server-side.
 */
const FrameworkChangelog: React.FC = () => {
  const { t } = useTranslation();
  const { granted, isLoading } = useHasFrameworkAccess();

  // Same credentialed fetch the My Products card uses, for the same reason:
  // the endpoint answers 403 when the EULA has not been accepted, and a plain
  // link would render that as a browser error page instead of saying so.
  const downloads = useDownloadTracker();

  const downloadVersion = async (downloadFor: string, os: string) => {
    const response = await fetch(
      managementService.getFrameworkPackageUrl(os, downloadFor),
      { credentials: 'include' }
    );
    if (!response.ok) {
      // The changelog is reachable on the grant alone, but the bytes need the
      // clickwrap, which lives on the My Products card.
      toast.error(
        response.status === 403
          ? t('management.framework.eulaRequired')
          : t('management.framework.downloadError')
      );
      return;
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition');
    const filename =
      disposition?.match(/filename="?([^";]+)"?/i)?.[1] ??
      `sbframework_${downloadFor}_${os}.zip`;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
    toast.success(t('download.started'));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-[80px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Rendered in place rather than redirected to. A redirect rewrites the
  // URL, so a holder who lands here while the session is still resolving
  // ends up parked on /not-found with no way back to the page they asked
  // for. Rendering NotFound here leaves the address alone, so the real
  // page appears the moment access is known.
  if (!granted) return <NotFound />;

  return (
    <ProductChangelog
      productName="Squarebit Framework"
      version={
        (pkg as { sbframework?: { version?: string } }).sbframework?.version ??
        '0.0.0'
      }
      mdPath="/changelog-sbframework.md"
      publicRepo={null}
      downloadPlatforms={[
        { os: 'windows', label: 'Windows', icon: <FaWindows size={13} /> },
        { os: 'mac', label: 'macOS', icon: <FaApple size={13} /> }
      ]}
      onDownloadVersion={(downloadFor, os) => {
        void downloads.run(`${downloadFor}-${os}`, () =>
          downloadVersion(downloadFor, os)
        );
      }}
      downloadingKey={downloads.busyKey}
    />
  );
};

export default FrameworkChangelog;
